import Dialog from "../../base/dialog";
import {Box, Card, DialogActions, DialogContent, DialogTitle, Divider, SvgIcon, Typography} from "@mui/material";
import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {Collapse, Row} from "reactstrap";
import Button from "../../base/button/button";
import Api from "../../../../core/services/api/api";
import Utils, {numComparator} from "../../../../core/services/utils/utils";
import useIsMounted from "../../../hooks/use-is-mounted";
import LoadingLottieAnimation from "../../app-specific/lottie-animations/loading";
import Grid from "@mui/material/Unstable_Grid2";
import IconButton from "../../base/button/icon_button";
import {FileCopy, FileCopyOutlined} from "@mui/icons-material";
import classnames from "classnames";
import {ReactComponent as ToggleIcon} from "../../../../assets/images/warranty-statuses-reasons/toggle.svg";
import DateUtils from "../../../../core/services/date-utils";
import {useBizFormApiResolver} from "../../../hooks/use-biz-form-api-resolver";
import {darken, lighten} from "@mui/system/colorManipulator";
import {QuestionAnswer} from "../view-form-response/question-answer-section";


const steps = {
    statusSelection: 'statusSelection',
    formResponseReview: 'formResponseReview',
}


const UpdateNcrStatusDialog = ({
                                   open,
                                   setOpen,
                                   statusId,
                                   onSuccess,
                                   statusHistory,
                               }) => {
    const [loadingStatuses, setLoadingStatuses] = useState(false);
    const [statuses, setStatuses] = useState([]);
    const [fetchedFormInfo, setFetchedFormInfo] = useState({});
    const [loadingFormInfo, setLoadingFormInfo] = useState({});
    const [selectedStatusId, setSelectedStatusId] = useState(statusId);
    const [selectedFormResponseId, setSelectedFormResponseId] = useState();
    const [openedHistory, setOpenedHistory] = useState();
    const [step, setStep] = useState(steps.statusSelection);
    const isMounted = useIsMounted();

    const ncrBizFormApi = useBizFormApiResolver((t) => t.ncrService);
    const cacheRef = useRef({});

    const statusHasForm = useMemo(() => !!statuses.find(s => s.id === selectedStatusId)?.formReference, [statuses, selectedStatusId]);
    const statusesToChooseFrom = useMemo(() => {
        return statuses?.map(e => ({
            ...e,
            past: statusHistory?.some(h => h.statusId === e.id) ?? false
        })) ?? [];
    }, [statusHistory, statuses]);
    const hasStatuses = useMemo(() => !!statusesToChooseFrom.length, [statusesToChooseFrom.length]);

    const selectedStatusHistory = useMemo(() => {
        const res = statusHistory?.filter(h => h.statusId === selectedStatusId && !!h.formResponseReference) ?? [];
        res.sort((a, b) => Utils.dateComparator(b.submitDateTime, a.submitDateTime, 'seconds'));
        return res;
    }, [statusHistory, selectedStatusId]);

    /**
     * Fetches the ncr statuses of the system.
     * @type {(function(): Promise<void>)}
     */
    const getStatuses = useCallback(async () => {
        setLoadingStatuses(true);
        const response = await Api.listNcrStatuses();
        if (!isMounted())
            return;
        setLoadingStatuses(false);
        if (response?.resultFlag) {
            const res = [...response.data];
            res.sort((a, b) => numComparator(a.orderIndex, b.orderIndex));
            setStatuses(res);
        }
    }, [isMounted])

    /**
     * As soon as the dialog is opened:
     * - Get the statuses
     */
    useEffect(() => {
        if (!open)
            return;
        void getStatuses();
    }, [getStatuses, open]);

    /**
     * Resets the state of the dialog when it is opened.
     */
    useEffect(() => {
        if (open) {
            setStatuses([]);
            setStep(steps.statusSelection);
            setSelectedStatusId(statusId);
            setFetchedFormInfo({});
            setLoadingFormInfo({});
            cacheRef.current = {};
            setOpenedHistory(undefined);
            setSelectedFormResponseId(undefined)
        }
    }, [open, statusId])

    /**
     * Fetches the form info of the form reference and form response reference
     * @type {(function(*, *): Promise<void>)}
     */
    const fetchFormInfo = useCallback(async (formReference, formResponseReference) => {
        if (cacheRef.current[formResponseReference]) {
            return;
        }
        setLoadingFormInfo(prevState => ({...prevState, [formResponseReference]: true}));
        const [formResponse, formResponseResponse] = await Promise.all([
            ncrBizFormApi.getForm({id: formReference}),
            ncrBizFormApi.getFormResponse({id: formResponseReference}),
        ])
        if (!isMounted())
            return;
        setLoadingFormInfo(prevState => ({...prevState, [formResponseReference]: false}));
        const questions = formResponse?.data?.questions ?? [];
        const answers = formResponseResponse?.data?.questionResponses ?? [];
        if (formResponse?.resultFlag) {
            setFetchedFormInfo(prevState => {
                const cache = ({
                    ...prevState,
                    [formResponseReference]: questions.map((question) => {
                        const answer = answers.find((answer) => answer.questionId === question.id);
                        return {question: question, answer: answer}
                    })
                })
                cacheRef.current = cache;
                return cache;
            });
        }
    }, [isMounted, ncrBizFormApi])

    /**
     * Fetches the form info of the selected status history.
     */
    useEffect(() => {
        if (!openedHistory || !open)
            return;
        void fetchFormInfo(openedHistory.formReference, openedHistory.formResponseReference);
    }, [fetchFormInfo, openedHistory, open])

    /**
     * Displays the prev step of the dialog.
     * @type {(function(): void)}
     */
    const prevStep = useCallback(() => {
        if (step === steps.statusSelection) {
            setOpen(false);
            return;
        }
        setStep(steps.statusSelection);
    }, [step, setOpen]);

    /**@type {boolean}*/
    const nextStepDisabled = useMemo(() => {
        const status = statuses.find(s => s.id === selectedStatusId);
        if (!status)
            return true;
        return status.id === statusId;
    }, [selectedStatusId, statusId, statuses]);

    /**
     * Displays the next step of the dialog
     * @type {(function(): void)}
     */
    const nextStep = useCallback(() => {
        const status = statuses.find(s => s.id === selectedStatusId);
        if (step === steps.statusSelection) {
            if (!statusHasForm || !selectedStatusHistory.length) {
                if (status.id === statusId) {
                    // If the status is the same as the current status, close the dialog.
                    return setOpen(false);
                }
                onSuccess?.(status.id, status.title, status.formReference, status.formResponseReference);
            } else {
                setStep(steps.formResponseReview);
            }
        } else if (step === steps.formResponseReview) {
            onSuccess?.(status.id, status.title, status.formReference, selectedFormResponseId);
        }
    }, [onSuccess, selectedFormResponseId, selectedStatusHistory.length, selectedStatusId, setOpen, statusHasForm, statusId, statuses, step])

    return (
        <>
            <Dialog
                open={open ?? false}
                setOpen={setOpen}
                maxWidth={'sm'}
                className={'bound-dialog update-ncr-status-dialog'}
                scroll={'paper'}
                aria-labelledby="scroll-dialog-title"
            >
                <DialogTitle id={'scroll-dialog-title'}>
                    <p className={`text-lg text-color-1 my-3 font-weight-bold text-center`}>
                        {
                            step === steps.statusSelection &&
                            "Update NCR Status"
                        }
                        {
                            step === steps.formResponseReview &&
                            "Review Questionnaire"
                        }
                    </p>
                    <Divider className='divider'/>
                </DialogTitle>
                <DialogContent>
                    <Box sx={{display: 'flex', flexDirection: 'column', padding: 4}}>
                        {
                            loadingStatuses &&
                            <LoadingLottieAnimation
                                width={'80%'}
                                className={'my-5'}
                            />
                        }
                        {
                            !loadingStatuses &&
                            step === steps.statusSelection &&
                            <Grid container spacing={2}>
                                {
                                    !hasStatuses &&
                                    <Typography variant={'body2'} className={'mb-3'}>
                                        There are no statuses to update to. please create new statuses first in the NCR Settings page.
                                    </Typography>
                                }
                                {
                                    statusesToChooseFrom
                                        .map(status =>
                                            <Grid key={status.id} xs={12}>
                                                <Card
                                                    variant={'outlined'}
                                                    onClick={() => setSelectedStatusId(status.id)}
                                                    sx={{
                                                        cursor: 'pointer',
                                                        padding: 2,
                                                        transition: 'all 0.3s',
                                                        ...(status.past && {
                                                            borderColor: 'success.main',
                                                            backgroundColor: (theme) => theme.palette.mode === 'dark' ? darken(theme.palette.success.main, 0.85) : lighten(theme.palette.success.main, 0.85),
                                                        }),
                                                        ...(selectedStatusId === status.id && {
                                                            borderColor: 'primary.main',
                                                            backgroundColor: (theme) => theme.palette.mode === 'dark' ? darken(theme.palette.primary.main, 0.85) : lighten(theme.palette.primary.main, 0.85),
                                                        }),
                                                    }}
                                                >
                                                    <Typography
                                                        variant={'body2'}
                                                        fontWeight={600}
                                                        color={selectedStatusId === status.id ? 'primary.main' : status.past ? 'success.main' : 'text.primary'}
                                                    >
                                                        {status.title}
                                                        {
                                                            statusId === status.id &&
                                                            <Typography
                                                                component={'span'}
                                                                variant={'body2'}
                                                                fontWeight={600}
                                                                color={'secondary.main'}
                                                                sx={{marginInlineStart: 1}}
                                                            >
                                                                (Lastest completed status)
                                                            </Typography>
                                                        }
                                                    </Typography>

                                                </Card>
                                            </Grid>
                                        )
                                }
                            </Grid>
                        }
                        {
                            !loadingStatuses &&
                            step === steps.formResponseReview &&
                            <>
                                <Typography variant={'body2'} className={'mb-3'}>
                                    You have already submitted a questionnaire for this status. You may review your previous questionnaire
                                    answers
                                    and select them for convenience. You may also submit a new questionnaire using the provided button at
                                    the
                                    bottom.
                                </Typography>
                                <Grid container spacing={2}>
                                    {
                                        selectedStatusHistory.map(history => {
                                                const selected = selectedFormResponseId === history.formResponseReference;
                                                const opened = openedHistory === history;
                                                return (
                                                    <Grid key={history.formResponseId} xs={12}>
                                                        <Card
                                                            variant={'outlined'}
                                                            sx={{
                                                                transition: 'all 0.3s',
                                                                ...(selected && {
                                                                    borderColor: 'primary.main',
                                                                    backgroundColor: 'primary.100',
                                                                })
                                                            }}
                                                        >
                                                            <Grid container spacing={2} marginInline={0} marginBlock={0}>
                                                                <Grid xs sx={{display: 'flex', alignItems: 'center'}}>
                                                                    <Typography variant={'body2'} fontWeight={600}>
                                                                        Submitted @ {
                                                                        history.submitDateTime
                                                                            ? DateUtils.serverToLocal(history.submitDateTime).format(DateUtils.dateTimeFormat)
                                                                            : '--'
                                                                    }
                                                                    </Typography>
                                                                </Grid>
                                                                <Grid xs={'auto'}>
                                                                    <Box sx={{
                                                                        display: 'flex',
                                                                        justifyContent: 'flex-end',
                                                                        alignItems: 'center',
                                                                        gap: 0.5
                                                                    }}>
                                                                        <IconButton
                                                                            onPressed={() => setSelectedFormResponseId(history.formResponseReference)}
                                                                            color={'paper.primary'}
                                                                        >
                                                                            {
                                                                                selected
                                                                                    ? <FileCopy/>
                                                                                    : <FileCopyOutlined/>
                                                                            }
                                                                        </IconButton>
                                                                        <IconButton
                                                                            className={'toggle-button'}
                                                                            onPressed={() => setOpenedHistory(prevStep => prevStep === history ? undefined : history)}
                                                                        >
                                                                            <SvgIcon
                                                                                component={ToggleIcon}
                                                                                className={classnames('icon normal toggle-icon', {'open': opened})}
                                                                            />
                                                                        </IconButton>
                                                                    </Box>
                                                                </Grid>
                                                                <Grid xs={12} sx={{...(opened && {padding: 0})}}>
                                                                    <Collapse isOpen={opened}>
                                                                        <div className={'d-flex flex-column p-2'} style={{gap: 24}}>
                                                                            {
                                                                                loadingFormInfo[history.formResponseReference] &&
                                                                                Array.from({length: 3}).map((_, index) => (
                                                                                    <QuestionAnswer key={index} loading/>
                                                                                ))
                                                                            }
                                                                            {
                                                                                !loadingFormInfo[history.formResponseReference] &&
                                                                                fetchedFormInfo[history.formResponseReference]?.map((info) => (
                                                                                    <QuestionAnswer
                                                                                        key={info.question?.id}
                                                                                        question={info.question}
                                                                                        answer={info.answer}
                                                                                    />
                                                                                ))
                                                                            }
                                                                        </div>
                                                                    </Collapse>
                                                                </Grid>
                                                            </Grid>
                                                        </Card>
                                                    </Grid>
                                                )
                                            }
                                        )
                                    }
                                </Grid>
                            </>
                        }
                    </Box>
                </DialogContent>
                <DialogActions className={'p-0'}>
                    <Row className={'w-100 no-gutters'}>
                        <Divider className='divider'/>
                        <Row className='no-gutters justify-content-between align-items-center w-100'>
                            <Button
                                variant={'dialog'}
                                color={'primary'}
                                onClick={prevStep}
                                borderRadius={'0 8px 0 8px'}
                            >
                                {
                                    step === steps.statusSelection &&
                                    "Cancel"
                                }
                                {
                                    step === steps.formResponseReview &&
                                    "Back"
                                }
                            </Button>
                            <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'flex-end'}}>
                                {
                                    step === steps.formResponseReview &&
                                    selectedFormResponseId &&
                                    <Button
                                        variant={'dialog'}
                                        color={'primary'}
                                        onClick={nextStep}
                                        borderRadius={'8px 0 0 0'}
                                    >
                                        Use selected
                                    </Button>
                                }
                                {
                                    hasStatuses &&
                                    <Button
                                        variant={'dialog'}
                                        color={'primary'}
                                        onClick={nextStep}
                                        disabled={nextStepDisabled}
                                        borderRadius={`${step === steps.formResponseReview && selectedFormResponseId ? '0' : '8px'} 0 8px 0`}
                                    >
                                        {
                                            step === steps.statusSelection &&
                                            (statusHasForm ? "Next" : "Update")
                                        }
                                        {
                                            step === steps.formResponseReview &&
                                            (statusHasForm ? "Submit new" : "Update")
                                        }
                                    </Button>
                                }
                            </Box>
                        </Row>
                    </Row>
                </DialogActions>
            </Dialog>
        </>
    );
}


export default UpdateNcrStatusDialog;
